import {
	IAppInfoModalConfig,
	TAppInfoModalType,
} from "../Elements/Global/molecules/Modals/InfoModal/Interfaces"
import { TInternalFormName, TPTKMInternalFormName } from "../Interfaces/AdminInterface"
import { IMainField } from "../Interfaces/DataInterface"
import { getLastViewedSection } from "../Templates/UserOverviewPage"

export default function createStore() {
	return {
		/** Is HD instance configured for anonymous users */
		instanceAnonymousUsers: false,
		setInstanceAnonymousUsers(bool: boolean) {
			this.instanceAnonymousUsers = bool
		},
		isInAdminSection: false,
		setIsInAdminSection(bool: boolean) {
			this.isInAdminSection = bool
		},
		isInCnfMenu: false,
		setIsInCnfMenu(bool: boolean) {
			this.isInCnfMenu = bool
		},
		sectionIsInView: sectionIsInView,
		setSectionIsInView(sectionRef: TSectionRef) {
			this.sectionIsInView = { ...this.sectionIsInView, [sectionRef.section]: sectionRef.inView }
		},
		userIsAuthorized: true,
		setUserIsAuthorized(bool: boolean) {
			this.userIsAuthorized = bool
		},
		userHasAdminAccess: false,
		setUserHasAdminAccess(bool: boolean) {
			this.userHasAdminAccess = bool
		},
		hasAdminRoute: false,
		setHasAdminRoute(bool: boolean) {
			this.hasAdminRoute = bool
		},
		isIIS: false,
		setIsIIS(bool: boolean) {
			this.isIIS = bool
		},
		isToken: false,
		setIsToken(bool: boolean) {
			this.isToken = bool
		},
		
		infoModalConfig: infoModalConfig,
		/**
		 * By default, shows the info message
		 * (`show` property `true`, if `undefined`).
		 */
		setInfoModalConfig({
			titleKey,
			descriptionKey,
			show,
			showCancelBtn,
			type,
			showDuration,
			cancelTextKey,
			proceedTextKey,
			onCancel,
			onProceed,
		}: Partial<IAppInfoModalConfig> & {
			// Mandatory properties to provide for this setter function
			titleKey: string
			type: TAppInfoModalType
		}) {
			this.infoModalConfig = {
				titleKey,
				descriptionKey: descriptionKey ?? defaultInfoModalConfig.descriptionKey,
				show: show ?? true, // !Attention: The only property that does not utilize default config
				showCancelBtn: showCancelBtn ?? defaultInfoModalConfig.showCancelBtn,
				type,
				showDuration: showDuration ?? defaultInfoModalConfig.showDuration,
				cancelTextKey: cancelTextKey ?? defaultInfoModalConfig.cancelTextKey,
				proceedTextKey: proceedTextKey ?? defaultInfoModalConfig.proceedTextKey,
				onCancel: onCancel ?? defaultInfoModalConfig.onCancel,
				onProceed: onProceed ?? defaultInfoModalConfig.onProceed,
			}
		},
		/**
		 * Resets AppInfoModal config to its default state
		 */
		resetInfoModalConfig() {
			return (this.infoModalConfig = defaultInfoModalConfig)
		},

		/**
		 * Overlay
		 */
		isLoaderOverlayOpen: false,
		setLoaderOverlayOpen(bool: boolean) {
			return (this.isLoaderOverlayOpen = bool)
		},
		inputIsLoading: inputIsLoading,
		startInputLoading(inputName: string) {
			this.inputIsLoading = { ...this.inputIsLoading, [inputName]: true }
		},
		stopInputLoading(inputName: string) {
			this.inputIsLoading = { ...this.inputIsLoading, [inputName]: false }
		},

		/**
		 * A bunch of triggers
		 * (purposely sectioned off from rest of properties)
		 */
		/**
		 * session Timeout update
		 */
		sessionTimeout: false,
		triggerSessionTimeout() {
			return (this.sessionTimeout = !this.sessionTimeout)
		},
		/**
		 * testConnection
		 */
		testConnection: false,
		triggerTestConnection() {
			return (this.testConnection = !this.testConnection)
		},
		setTestConnection(bool: boolean) {
			return (this.testConnection = bool)
		},
		/**
		 * jamaConnection
		 */
		hasConnectionBeenMade: false,
		setHasConnectionBeenMade(bool: boolean) {
			return (this.hasConnectionBeenMade = bool)
		},
		removeConnection() {
			this.setHasConnectionBeenMade(false)
		},
		/**
		 * show admin settings modal
		 */
		showAdminSettingsModal: false,
		setShowAdminSettingsModal(bool: boolean) {
			return (this.showAdminSettingsModal = bool)
		},
		/**
		 * admin settings modal section
		 */
		adminSettingsSection: adminSettingsSection,
		setAdminSettingsSection(name: TAdminSettingSection) {
			return (this.adminSettingsSection = name)
		},
		resetAdminSettingsSection() {
			return (this.adminSettingsSection = adminSettingsSection)
		},
		/**
		 * selectedToolId,
		 * which dictates `showAdminPanelForms`
		 */
		selectedToolId: selectedToolId,
		showAdminPanelForms: false,
		setSelectedToolId(toolId: typeof selectedToolId) {
			this.showAdminPanelForms = toolId != null
			this.selectedToolId = toolId
		},
		selectedField: selectedField,
		setSelectedField(field: IMainField | undefined) {
			return (this.selectedField = field)
		},
		/**
		 * Form section has an array of fields.
		 * Each field has Id
		 * To track changes in the specific sections field, id needs to be stored.
		 */
		activeSectionId: activeSectionId,
		setActiveSectionId(sectionId: number | null) {
			return (this.activeSectionId = sectionId)
		},
		/**
		 * triggeredFieldChange
		 */
		triggeredFieldChange: triggeredFieldChange,
		setTriggeredFieldChange(fieldName: string) {
			const fieldObj = {
				[fieldName]: {
					timeMS: new Date().getMilliseconds(),
					fieldName: fieldName,
				},
			}
			return (this.triggeredFieldChange = fieldObj)
		},

		/**
		 * when BE lost user session
		 */
		sessionRelod: false,
		triggerSessionRelod() {
			return (this.sessionRelod = !this.sessionRelod)
		},
		/**
		 * when logo is uploaded successfully
		 */
		logoUploaded: false,
		triggerLogoUploaded() {
			return (this.logoUploaded = !this.logoUploaded)
		},
		/**
		 *
		 */
		triggerGoToFirstPage: false,
		setTriggerGoToFirstPage() {
			return (this.triggerGoToFirstPage = !this.triggerGoToFirstPage)
		},
		/**
		 * Icon before saving settings
		 */
		tempIconUrl: "",
		setTempIconUrl(url: string) {
			this.tempIconUrl = url
		},
		// Hover logic, position tracking for table cells
		hoveredCellOffsetLeft: hoveredCellOffsetLeft,
		setHoveredCellOffsetLeft(val: number | null) {
			this.hoveredCellOffsetLeft = val
		},
		isTableCellInputFocused: false,
		setIsTableCellInputFocused(bool: boolean) {
			this.isTableCellInputFocused = bool
		},

		changeConnection: false,
		setChangeConnection(bool: boolean) {
			return (this.changeConnection = bool)
		},

		selectedUserUISection: selectedUserUISection,
		setSelectedUserUISection(section: TUserUISection) {
			this.selectedUserUISection = section
		},

		subNavElement: subNavElement,
		setSubNavElement(el: JSX.Element | undefined) {
			this.subNavElement = el
		},
	}
}
const subNavElement: JSX.Element | undefined = undefined

const hoveredCellOffsetLeft: number | null = null

/**
 * Default objects
 */ /** */
const defaultInfoModalConfig: IAppInfoModalConfig = {
	titleKey: "",
	descriptionKey: "",
	show: false,
	showCancelBtn: false,
	type: "info",
	showDuration: -1,
	cancelTextKey: "infoModal.no",
	proceedTextKey: "infoModal.ok",
	onCancel: () => {},
	onProceed: () => {},
}

/**
 * This way we can assign types to objects / multiple types to properties,
 * whereas, if we put the property directly in the store,
 * it (currently) cannot be given a desired type, and it is inferred.
 *
 * Convention:
 * name the constant the same as its property name in the store.
 */ /** */
const infoModalConfig: IAppInfoModalConfig = defaultInfoModalConfig
const activeSectionId: number | null = null
const inputIsLoading: { [key: string]: boolean } = {}
// !TECHNICAL DEBT: A tool's id cannot be a string, it is a specific type
const selectedToolId: string | undefined = undefined
const adminSettingsSection: TAdminSettingSection = "email"
const triggeredFieldChange: TFieldChange | undefined = undefined
const selectedField: IMainField | undefined = undefined
const selectedUserUISection: TUserUISection = getLastViewedSection()

export type TFieldChange = {
	[fieldName: string]: {
		timeMS: number
		fieldName: string
	}
}

export type TUserUISection = "newRequest" | "myRequests"
export type TAdminSettingSection = "email" | "personalize" | "additional"

export type TSectionRef = {
	section: TInternalFormName | TPTKMInternalFormName
	inView: boolean
}

export const sectionIsInView: Partial<{ [key in TInternalFormName | TPTKMInternalFormName]: any }> =
	{}
